import { createContext, useContext, useReducer } from 'react'

import type { Dispatch, PropsWithChildren, ReactElement } from 'react'
import type { TListingMode, TFilterPreset } from '~/types/cluster'
import type { TExtendedDeviceType } from '~/types/device-type'

export type Filter = 'deviceType' | 'filterPreset' | 'listingMode' | 'nbVisibleApps' | 'searchElements' | 'tags'

export interface IActionReducer {
  type: Filter
  value?: unknown
}

export interface IClustersContext {
  dispatch: Dispatch<IActionReducer>
  state: IStateReducer
}

export interface IStateReducer {
  deviceType: TExtendedDeviceType
  filterPreset: TFilterPreset
  listingMode: TListingMode
  nbVisibleApps: number
  searchElements: string[]
  tags: string[]
}

const searchParams = new URLSearchParams(location.search)

const reducer = (state: IStateReducer, action: IActionReducer) => {
  switch (action.type) {
    case 'deviceType':
      return { ...state, deviceType: action.value as TExtendedDeviceType }
    case 'listingMode':
      return { ...state, listingMode: action.value as TListingMode }
    case 'filterPreset':
      return { ...state, filterPreset: action.value as TFilterPreset }
    case 'nbVisibleApps':
      return { ...state, nbVisibleApps: action.value as number }
    case 'searchElements':
      return { ...state, searchElements: action.value as string[] }
    case 'tags':
      return { ...state, tags: action.value as string[] }
    default:
      return state
  }
}

export const vanillaState: IStateReducer = {
  deviceType: 'ios',
  filterPreset: 'all',
  listingMode: 'cluster',
  nbVisibleApps: 0,
  searchElements: [],
  tags: [],
}

export const initialState: IStateReducer = {
  deviceType: (searchParams.get('deviceType') as TExtendedDeviceType) || vanillaState.deviceType,
  filterPreset: (searchParams.get('filterPreset') as TFilterPreset) || vanillaState.filterPreset,
  listingMode: (searchParams.get('listingMode') as TListingMode) || vanillaState.listingMode,
  nbVisibleApps: vanillaState.nbVisibleApps,
  searchElements:
    searchParams.getAll('searchElements[]').length > 0
      ? searchParams.getAll('searchElements[]')[0].split(',')
      : vanillaState.searchElements,
  tags: searchParams.getAll('tags[]').length > 0 ? searchParams.getAll('tags[]')[0].split(',') : vanillaState.tags,
}

export const ClustersContext = createContext<IClustersContext>({
  dispatch: (): IStateReducer => initialState,
  state: initialState,
})

const ClustersContextProvider = ({ children }: PropsWithChildren): ReactElement => {
  const [state, dispatch] = useReducer(reducer, initialState)

  return <ClustersContext.Provider value={{ dispatch, state }}>{children}</ClustersContext.Provider>
}

const useClustersContext = (): IClustersContext => {
  const context = useContext(ClustersContext)

  if (context === undefined) {
    throw new Error('useClustersContext was used outside of its Provider')
  }

  return context
}

export { ClustersContextProvider, useClustersContext }
